import { ComponentProps, forwardRef, ElementType, ReactNode } from "react";

export type Props = ComponentProps<"label"> & {
  /** Element or component to render as */
  as?: ElementType;
  /** Shows required indicator */
  required?: boolean;
  /** Sets aria-invalid for error state */
  error?: boolean;
  /** Label text */
  children?: ReactNode;
  /** Custom required indicator renderer */
  renderRequired?: () => ReactNode;
  /** Additional props passed to the underlying element */
  [key: string]: unknown;
};

/**
 * Base label component with no styling.
 * Use this as the foundation for styled label components.
 *
 * Supports polymorphic rendering via the `as` prop.
 */
export const LabelBase = forwardRef<HTMLLabelElement, Props>(
  (
    {
      as = "label",
      required = false,
      error,
      children,
      renderRequired,
      ...props
    },
    ref
  ) => {
    const requiredIndicator = required ? (
      typeof renderRequired === "function" ? renderRequired() : <span>*</span>
    ) : null;

    const Component = as as ElementType;

    return (
      <Component
        ref={ref}
        aria-invalid={error || undefined}
        {...props}
        {...(props.id ? { "data-testid": `label-${props.id}` } : {})}
      >
        {children}
        {requiredIndicator}
      </Component>
    );
  }
);

LabelBase.displayName = "LabelBase";

